import React, { useEffect, useState } from 'react';
import CustomSelect from '../../ui/CustomSelect';
import Loader from '../../../loader/Loader';

const PHONE_PATTERN = /^9715[0-9]{8}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

export default function IDEnquiryStep({
  idEnquiryPhone,
  setIdEnquiryPhone,
  idEnquiryEmail,
  setIdEnquiryEmail,
  idEnquiryFirstName,
  setIdEnquiryFirstName,
  idEnquiryLastName,
  setIdEnquiryLastName,
  idEnquiryBuilding,
  setIdEnquiryBuilding,
  idEnquiryArea,
  setIdEnquiryArea,
  idEnquiryCity,
  setIdEnquiryCity,
  idEnquiryStore,
  setIdEnquiryStore,
  idEnquiryComments,
  setIdEnquiryComments,
  StoreLocation,
  showErrors,
  phoneError,
  setPhoneError,
  emailError,
  setEmailError,
}) {
  const [areaOptions, setAreaOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isCityLoading, setIsCityLoading] = useState(false);

  useEffect(() => {
    const fetchAreaCodes = async () => {
      let allAreaCodes = [];
      let offset = 0;
      let hasMore = true;
      while (hasMore) {
        try {
          const res = await fetch(`/server/ikea_services_function/areacode?offset=${offset}`);
          const json = await res.json();
          if (json.status === 'success') {
            const items = json.data.Areacode.map((item) => ({
              value: item.AreaCode,
              label: item.AreaCode,
            }));
            allAreaCodes = [...allAreaCodes, ...items];
            hasMore = json.data.hasMore;
            offset += json.data.Areacode.length;
          } else {
            hasMore = false;
          }
        } catch {
          hasMore = false;
        }
      }
      setAreaOptions(allAreaCodes);
    };
    fetchAreaCodes();
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      if (!idEnquiryArea) {
        setCityOptions([]);
        return;
      }
      setIsCityLoading(true);
      let allCities = [];
      let offset = 0;
      let hasMore = true;
      while (hasMore) {
        try {
          const res = await fetch(
            `/server/ikea_services_function/citylist?areacode=${idEnquiryArea.value}&offset=${offset}`
          );
          const json = await res.json();
          if (json.status === 'success') {
            const items = json.data.CityList.map((item) => ({
              value: item.City,
              label: item.City,
            }));
            allCities = [...allCities, ...items];
            hasMore = json.data.hasMore;
            offset += json.data.CityList.length;
          } else {
            hasMore = false;
          }
        } catch {
          hasMore = false;
        }
      }
      setCityOptions(allCities);
      setIsCityLoading(false);
    };
    fetchCities();
  }, [idEnquiryArea]);

  const handlePhoneBlur = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    const full = `971${digits}`;
    if (!PHONE_PATTERN.test(full)) {
      setPhoneError('Enter a valid UAE phone number (e.g., 501234567)');
    } else {
      setPhoneError('');
      setIdEnquiryPhone(digits);
    }
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setIdEnquiryEmail(val);
    setEmailError(EMAIL_PATTERN.test(val) ? '' : 'Enter a valid email address');
  };

  return (
    <div className="step-2">
      <div className="customer-info">

        <div className="form-group">
          <label className="required">Name</label>
          <div className="input-group">
            <div className={`field-wrapper ${showErrors && !idEnquiryFirstName.trim() ? 'has-error' : ''}`}>
              <input
                type="text"
                placeholder="First Name"
                value={idEnquiryFirstName}
                onChange={(e) => setIdEnquiryFirstName(e.target.value)}
              />
              {showErrors && !idEnquiryFirstName.trim() && (
                <p className="error-text">First name is required</p>
              )}
            </div>
            <div className={`field-wrapper ${showErrors && !idEnquiryLastName.trim() ? 'has-error' : ''}`}>
              <input
                type="text"
                placeholder="Last Name"
                value={idEnquiryLastName}
                onChange={(e) => setIdEnquiryLastName(e.target.value)}
              />
              {showErrors && !idEnquiryLastName.trim() && (
                <p className="error-text">Last name is required</p>
              )}
            </div>
          </div>
        </div>

        <div className={`form-group ${showErrors && (!idEnquiryPhone || phoneError) ? 'has-error' : ''}`}>
          <label className="required">Phone Number</label>
          <div className="phone-input-wrapper">
            <span className="phone-prefix">+971</span>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={9}
              placeholder="501234567"
              defaultValue={idEnquiryPhone}
              onBlur={handlePhoneBlur}
              className="phone-input"
            />
          </div>
          {phoneError && <p className="error-text">{phoneError}</p>}
        </div>

        <div className={`form-group ${showErrors && (!idEnquiryEmail || emailError) ? 'has-error' : ''}`}>
          <label className="required">Email</label>
          <input
            type="email"
            placeholder="Email address (e.g. name@domain.com)"
            value={idEnquiryEmail}
            onChange={handleEmailChange}
          />
          {emailError && <p className="error-text">{emailError}</p>}
        </div>

        <div className={`form-group ${showErrors && (!idEnquiryArea?.value || !idEnquiryCity?.value || !idEnquiryBuilding.trim()) ? 'has-error' : ''}`}>
          <label className="required">Address</label>
          <div className="more-group">
            <CustomSelect
              placeholder="Select Area"
              options={areaOptions}
              value={idEnquiryArea}
              onChange={(opt) => {
                setIdEnquiryArea(opt);
                setIdEnquiryCity(null);
              }}
            />

            <div>
              {isCityLoading ? (
                <div className="loader-container">
                  <Loader />
                </div>
              ) : (
                <CustomSelect
                  placeholder="Select City"
                  options={cityOptions}
                  value={idEnquiryCity}
                  onChange={setIdEnquiryCity}
                  isDisabled={!idEnquiryArea}
                />
              )}
            </div>

            <input
              type="text"
              placeholder="Building Name / Flat No. / Street"
              value={idEnquiryBuilding}
              onChange={(e) => setIdEnquiryBuilding(e.target.value)}
            />
          </div>
          {showErrors && !idEnquiryArea?.value && <p className="error-text">Area is required</p>}
          {showErrors && idEnquiryArea?.value && !idEnquiryCity?.value && <p className="error-text">City is required</p>}
          {showErrors && !idEnquiryBuilding.trim() && <p className="error-text">Building / Street is required</p>}
        </div>

        <div className={`form-group ${showErrors && !idEnquiryStore ? 'has-error' : ''}`}>
          <label className="required">Preferred Store</label>
          <select
            value={idEnquiryStore}
            onChange={(e) => setIdEnquiryStore(e.target.value)}
          >
            <option value="">Select a store</option>
            {StoreLocation.map((store) => (
              <option key={store.id} value={store.StoreLocation}>
                {store.StoreLocation}
              </option>
            ))}
          </select>
          {showErrors && !idEnquiryStore && <p className="error-text">Please select a store</p>}
        </div>

        <div className="form-group">
          <label>Comments</label>
          <textarea
            placeholder="Tell us about your interior design needs..."
            value={idEnquiryComments}
            onChange={(e) => setIdEnquiryComments(e.target.value)}
            rows={4}
            className="enquiry-textarea"
          />
        </div>

      </div>
    </div>
  );
}
